import {
  Activity,
  ArrowLeft,
  Ellipsis,
  Info,
  Sparkles,
  Keyboard,
  KeyRound,
  LayoutGrid,
  LogOut,
  Monitor,
  Moon,
  Network as NetworkIcon,
  Rocket,
  Search,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useConnectionState } from '../api/connection';
import { useAuth, usePermission } from '../auth/context';
import { NetworkName } from '../networks/NetworkName';
import { strings } from '../strings';
import { CloudAccountName } from './Breadcrumbs';
import { MenuButton, type MenuEntry } from './Menu';
import { railSection, runLabel, useRouteEntities, type RouteEntities } from './routeContext';
import { useShell } from './shellContext';
import { THEME_PREFERENCES, useTheme } from './theme';

const s = strings.shell;
const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;

interface Heading {
  title: ReactNode;
  context: ReactNode | null;
  back: string | null;
}

/** The app bar's title, context line and parent page (spec 6.12, phone layout item 1). */
function phoneHeading({ context, blueprint, blueprintId, deployment, run, network }: RouteEntities): Heading {
  switch (context.page) {
    case 'blueprints':
      return { title: s.pages.blueprints, context: null, back: null };
    case 'designer':
      return { title: blueprint?.name ?? s.pages.designer, context: s.pages.designer, back: '/blueprints' };
    case 'versions':
    case 'deployments':
      return {
        title: s.pages[context.page],
        context: blueprint?.name ?? null,
        back: blueprintId ? `/blueprints/${blueprintId}` : '/blueprints',
      };
    case 'all-deployments':
      return { title: s.pages.allDeployments, context: null, back: null };
    case 'deployment':
      return {
        title: deployment?.name ?? s.kind.deployment,
        context: blueprint?.name ?? null,
        back: blueprintId ? `/blueprints/${blueprintId}/deployments` : '/deployments',
      };
    case 'runs':
      return { title: s.pages.runs, context: null, back: null };
    case 'run':
      // Feature spec 17: a network run belongs to its network.
      if (run?.networkId)
        return {
          title: runLabel(run),
          context: network?.name ?? strings.networks.nav.kind,
          back: `/networks/${run.networkId}`,
        };
      return {
        title: run ? runLabel(run) : s.kind.run,
        context: blueprint && deployment ? `${blueprint.name} / ${deployment.name}` : null,
        back: run ? `/deployments/${run.deploymentId}` : '/runs',
      };
    case 'cloud-accounts':
      return { title: s.pages.cloudAccounts, context: null, back: null };
    case 'cloud-account':
      return {
        title: <CloudAccountName id={context.cloudAccountId} />,
        context: s.pages.cloudAccounts,
        back: '/cloud-accounts',
      };
    case 'about':
      return { title: s.pages.about, context: s.pages.help, back: null };
    case 'networks':
      return { title: strings.networks.nav.networks, context: null, back: null };
    case 'network':
      return {
        title: <NetworkName id={context.networkId} />,
        context: strings.networks.nav.networks,
        back: '/networks',
      };
    case 'whats-new':
      return { title: s.pages.whatsNew, context: s.pages.help, back: null };
    default:
      return { title: strings.product, context: null, back: null };
  }
}

/** The phone layout's app bar (spec 6.12): back, title and context, connection state, search and a menu. */
export function PhoneAppBar() {
  const { openPalette, openShortcuts } = useShell();
  const { signOut } = useAuth();
  const { preference, setPreference } = useTheme();
  const connection = useConnectionState();
  const heading = phoneHeading(useRouteEntities());
  const entries: MenuEntry[] = [
    {
      kind: 'action',
      id: 'shortcuts',
      label: s.shortcutsButton,
      icon: <Keyboard aria-hidden className="size-4" />,
      onSelect: openShortcuts,
    },
    { kind: 'separator', id: 'sep-theme' },
    ...THEME_PREFERENCES.map((choice): MenuEntry => {
      const Icon = THEME_ICON[choice];
      return {
        kind: 'radio',
        id: `theme-${choice}`,
        label: s.theme[choice],
        icon: <Icon aria-hidden className="size-4" />,
        checked: preference === choice,
        onSelect: () => setPreference(choice),
      };
    }),
  ];
  if (signOut) {
    entries.push(
      { kind: 'separator', id: 'sep-out' },
      {
        kind: 'action',
        id: 'sign-out',
        label: s.user.signOut,
        icon: <LogOut aria-hidden className="size-4" />,
        onSelect: signOut,
      },
    );
  }
  return (
    <header data-region="topbar" tabIndex={-1} className="phone-appbar">
      {heading.back ? (
        <Link to={heading.back} className="phone-icon-button" aria-label={s.phone.back} title={s.phone.back}>
          <ArrowLeft aria-hidden className="size-5" />
        </Link>
      ) : (
        <span aria-hidden className="phone-icon-spacer" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] leading-tight font-semibold">{heading.title}</p>
        {heading.context && <p className="truncate text-xs text-fg-muted">{heading.context}</p>}
      </div>
      {connection !== 'online' && (
        <span role="status" className="badge tone-warning">
          {connection === 'offline' ? s.status.offline : s.status.reconnecting}
        </span>
      )}
      <button
        type="button"
        className="phone-icon-button"
        aria-label={s.phone.search}
        title={s.phone.search}
        onClick={openPalette}
      >
        <Search aria-hidden className="size-5" />
      </button>
      <MenuButton
        buttonClassName="phone-icon-button"
        buttonLabel={s.phone.pageMenu}
        buttonTitle={s.phone.pageMenu}
        buttonContent={<Ellipsis aria-hidden className="size-5" />}
        menuLabel={s.phone.pageMenu}
        placement="below-end"
        entries={entries}
      />
    </header>
  );
}

function Tab({
  to,
  label,
  icon: Icon,
  current,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  current: boolean;
}) {
  return (
    <li className="flex-1">
      <Link to={to} className="phone-tab" aria-current={current ? 'page' : undefined}>
        <Icon aria-hidden className="size-5" />
        {label}
      </Link>
    </li>
  );
}

/** The phone layout's bottom tab bar (spec 6.12): the main navigation with More for the rest. */
export function PhoneTabBar() {
  const { config } = useAuth();
  const canOperate = usePermission('operate');
  const { pathname } = useLocation();
  const section = railSection(pathname);
  const more: MenuEntry[] = [
    // Feature spec 17: Networks is under More on phones, while networking is on.
    ...(config.networking?.enabled
      ? [
          {
            kind: 'link' as const,
            id: 'networks',
            label: strings.networks.nav.networks,
            to: '/networks',
            current: section === 'networks',
            icon: <NetworkIcon aria-hidden className="size-4" />,
          },
        ]
      : []),
    ...(canOperate
      ? [
          {
            kind: 'link' as const,
            id: 'cloud-accounts',
            label: s.rail.cloudAccounts,
            to: '/cloud-accounts',
            current: section === 'cloud-accounts',
            icon: <KeyRound aria-hidden className="size-4" />,
          },
        ]
      : []),
    {
      kind: 'link',
      id: 'about',
      label: s.rail.about,
      to: '/about',
      current: pathname === '/about',
      icon: <Info aria-hidden className="size-4" />,
    },
    {
      kind: 'link',
      id: 'whats-new',
      label: s.rail.whatsNew,
      to: '/whats-new',
      current: pathname === '/whats-new',
      icon: <Sparkles aria-hidden className="size-4" />,
    },
    { kind: 'note', id: 'version', label: s.rail.version(config.version) },
  ];
  return (
    <nav data-region="rail" tabIndex={-1} aria-label={s.rail.label} className="phone-tabbar">
      <ul className="flex w-full">
        <Tab
          to="/blueprints"
          label={s.rail.blueprints}
          icon={LayoutGrid}
          current={section === 'blueprints'}
        />
        <Tab to="/deployments" label={s.rail.deployments} icon={Rocket} current={section === 'deployments'} />
        <Tab to="/runs" label={s.rail.runs} icon={Activity} current={section === 'runs'} />
        <li className="flex flex-1">
          <MenuButton
            wrapperClassName="flex flex-1"
            buttonClassName="phone-tab"
            buttonCurrent={section === 'cloud-accounts' || section === 'networks' || section === 'help'}
            buttonContent={
              <>
                <Ellipsis aria-hidden className="size-5" />
                {s.phone.more}
              </>
            }
            menuLabel={s.phone.moreMenu}
            placement="above-end"
            entries={more}
          />
        </li>
      </ul>
    </nav>
  );
}
